import { getCurrentWatcher, scheduleWatchers } from './engine.js';
import { createHandler } from './create-handler.js';

const reactiveMap = new WeakMap();
const contextSet = new WeakSet();

const getProp = (props, target, key, value, proxy, context = proxy, deep, isArray) => {
    let prop = props.get(key);
    if (!prop) {
        if (typeof value === 'function' && isArray) {
            return;
        }

        const prevValue = target[key];
        const type =
            typeof prevValue === 'function' || typeof value === 'function' ? 'computedProperty' : 'reactiveProperty';

        prop = createHandler({
            target,
            key,
            proxy,
            context,
            type,
            fn: type === 'computedProperty' ? prevValue || value : undefined,
            deep,
        });
        props.set(key, prop);
    }

    return prop;
};

const deleteProp = (props, key) => {
    if (props.has(key)) {
        const prop = props.get(key);
        prop.dispose();

        props.delete(key);
    }
};

/**
 * @template T
 * @param {T} t
 * @param {{deep?:boolean, context: unknown}} param
 * @returns {T}
 */
const reactive = (t, { deep = true, context } = {}) => {
    if (typeof t !== 'object' || t === null) {
        return t;
    }

    if (reactiveMap.has(t)) {
        return t;
    }

    const isArray = Array.isArray(t);
    const props = new Map();

    const proxy = new Proxy(t, {
        get(target, key) {
            const prop = getProp(props, target, key, target[key], proxy, context, deep, isArray);

            const currentWatcher = getCurrentWatcher();

            if (prop && currentWatcher) {
                prop.addWatcher(currentWatcher);
                currentWatcher.addDependency(prop);
            }

            if (prop && prop.isComputedProperty && prop.dirty) {
                prop.invoke();
            }

            return target[key];
        },

        set(target, key, value) {
            const prevValue = target[key];

            if (!(isArray && key === 'length') && value === prevValue) {
                return true;
            }

            if (getCurrentWatcher()) {
                throw new Error("Not allowed to set a reactive property while it's watched");
            }

            const prop = getProp(props, target, key, value, proxy, context, deep, isArray);

            if (prop.isComputedProperty) {
                return true;
            }

            prop.set(value);
            scheduleWatchers(prop);

            return true;
        },

        deleteProperty(target, key) {
            deleteProp(props, key);
            delete target[key];
            return true;
        },
    });

    reactiveMap.set(proxy, props);

    if (!context) {
        contextSet.add(proxy);
    }

    if (deep) {
        if (!context) {
            context = proxy;
        }

        Object.keys(t).forEach((key) => {
            const value = t[key];
            if (typeof value === 'object' && value !== null) {
                t[key] = reactive(value, { deep, context });
            }
        });
    }

    return proxy;
};

const dispose = (target) => {
    if (!contextSet.has(target)) {
        return false;
    }
    disposeObject(target);
    contextSet.delete(target);

    return true;
};

const disposeObject = (target) => {
    // recursive dispose
    for (const [, value] of Object.entries(target)) {
        if (typeof value === 'object' && value !== null) {
            disposeObject(value);
        }
    }

    // cleanup props
    if (reactiveMap.has(target)) {
        const props = reactiveMap.get(target);
        props.forEach((value, key) => {
            deleteProp(props, key);
        });
    }
};

const isReactive = (target) => reactiveMap.has(target);

const isComputed = (target, key) => {
    const isReactive = reactiveMap.has(target);
    if (!isReactive) {
        return false;
    }

    // force prop creation
    target[key];

    const props = reactiveMap.get(target);
    if (!props.has(key)) {
        return false;
    }

    return props.get(key).isComputedProperty;
};

export { reactive, dispose, isReactive, isComputed };
